import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type {
  Club,
  ClubInvite,
  ClubInvitePreview,
  ClubMember,
  ClubRole,
  ClubWithMembership,
} from '@/types'

export const CLUBS_QUERY_KEY = 'clubs'

/** Clubs the current user belongs to, with member count and their role */
export function useMyClubs() {
  const { user } = useAuth()

  return useQuery({
    queryKey: [CLUBS_QUERY_KEY, user?.id],
    queryFn: async (): Promise<ClubWithMembership[]> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('clubs')
        .select('*, club_members(user_id, role)')
        .order('created_at', { ascending: false })

      if (error) {
        captureError(error, { context: 'useMyClubs', userId: user.id })
        throw error
      }

      return (data || []).map(({ club_members, ...club }) => {
        const members = (club_members || []) as { user_id: string; role: ClubRole }[]
        return {
          ...(club as Club),
          member_count: members.length,
          my_role: members.find((m) => m.user_id === user.id)?.role ?? 'member',
        }
      })
    },
    enabled: !!user?.id,
  })
}

export function useClub(clubId: string | undefined) {
  const { data: clubs, ...rest } = useMyClubs()
  return { ...rest, data: clubs?.find((c) => c.id === clubId) ?? null }
}

export function useClubMembers(clubId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [CLUBS_QUERY_KEY, user?.id, clubId, 'members'],
    queryFn: async (): Promise<ClubMember[]> => {
      const { data, error } = await supabase.rpc('get_club_members', { p_club_id: clubId })
      if (error) {
        captureError(error, { context: 'useClubMembers', clubId })
        throw error
      }
      return data || []
    },
    enabled: !!user?.id && !!clubId,
  })
}

export function useCreateClub() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      const { data, error } = await supabase.rpc('create_club', {
        p_name: name,
        p_description: description || null,
      })
      if (error) {
        captureError(error, { context: 'useCreateClub' })
        throw error
      }
      return data as string
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CLUBS_QUERY_KEY, user?.id] }),
  })
}

export function useUpdateClub() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({
      clubId,
      name,
      description,
    }: {
      clubId: string
      name: string
      description?: string | null
    }) => {
      const { error } = await supabase
        .from('clubs')
        .update({ name: name.trim(), description: description?.trim() || null })
        .eq('id', clubId)
      if (error) {
        captureError(error, { context: 'useUpdateClub', clubId })
        throw error
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CLUBS_QUERY_KEY, user?.id] }),
  })
}

export function useDeleteClub() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (clubId: string) => {
      const { error } = await supabase.from('clubs').delete().eq('id', clubId)
      if (error) {
        captureError(error, { context: 'useDeleteClub', clubId })
        throw error
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CLUBS_QUERY_KEY, user?.id] }),
  })
}

/** Removes a member; passing the current user's id leaves the club */
export function useRemoveClubMember() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ clubId, userId }: { clubId: string; userId: string }) => {
      const { error } = await supabase
        .from('club_members')
        .delete()
        .eq('club_id', clubId)
        .eq('user_id', userId)
      if (error) {
        captureError(error, { context: 'useRemoveClubMember', clubId })
        throw error
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CLUBS_QUERY_KEY, user?.id] }),
  })
}

export function useSetClubMemberRole() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({
      clubId,
      userId,
      role,
    }: {
      clubId: string
      userId: string
      role: ClubRole
    }) => {
      const { error } = await supabase
        .from('club_members')
        .update({ role })
        .eq('club_id', clubId)
        .eq('user_id', userId)
      if (error) {
        captureError(error, { context: 'useSetClubMemberRole', clubId })
        throw error
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CLUBS_QUERY_KEY, user?.id] }),
  })
}

/** The club's newest still-valid invite, if any */
export function useActiveClubInvite(clubId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [CLUBS_QUERY_KEY, user?.id, clubId, 'invite'],
    queryFn: async (): Promise<ClubInvite | null> => {
      const { data, error } = await supabase
        .from('club_invites')
        .select('*')
        .eq('club_id', clubId)
        .is('revoked_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) {
        captureError(error, { context: 'useActiveClubInvite', clubId })
        throw error
      }
      return data
    },
    enabled: !!user?.id && !!clubId,
  })
}

export function useCreateClubInvite() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (clubId: string): Promise<ClubInvite> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')
      const { data, error } = await supabase
        .from('club_invites')
        .insert({ club_id: clubId, created_by: user.id })
        .select()
        .single()
      if (error) {
        captureError(error, { context: 'useCreateClubInvite', clubId })
        throw error
      }
      return data
    },
    onSuccess: (invite) =>
      queryClient.invalidateQueries({
        queryKey: [CLUBS_QUERY_KEY, user?.id, invite.club_id, 'invite'],
      }),
  })
}

export function useRevokeClubInvite() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (invite: ClubInvite) => {
      const { error } = await supabase
        .from('club_invites')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', invite.id)
      if (error) {
        captureError(error, { context: 'useRevokeClubInvite', clubId: invite.club_id })
        throw error
      }
    },
    onSuccess: (_data, invite) =>
      queryClient.invalidateQueries({
        queryKey: [CLUBS_QUERY_KEY, user?.id, invite.club_id, 'invite'],
      }),
  })
}

export function useClubInvitePreview(token: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['club-invite', token, user?.id],
    queryFn: async (): Promise<ClubInvitePreview | null> => {
      const { data, error } = await supabase.rpc('get_club_invite', { p_token: token })
      if (error) {
        captureError(error, { context: 'useClubInvitePreview' })
        throw error
      }
      return data?.[0] ?? null
    },
    enabled: !!user?.id && !!token,
  })
}

export function useAcceptClubInvite() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.rpc('accept_club_invite', { p_token: token })
      if (error) {
        captureError(error, { context: 'useAcceptClubInvite' })
        throw error
      }
      return data as string
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CLUBS_QUERY_KEY, user?.id] }),
  })
}

/** Absolute URL to share for an invite */
export function inviteUrl(token: string) {
  return `${window.location.origin}/dashboard/clubs/join/${token}`
}
